/**
 * Browser-safe public configuration (Vite `VITE_*` only).
 *
 * Set `VITE_*` in `.env.development`, `.env.production`, or CI. `VITE_WS_BASE_URL` wins when
 * set. If it is omitted but `VITE_API_BASE_URL` is set, the WebSocket URL is derived from the
 * same origin (http→ws, https→wss) so typed chat and voice stay on the same backend.
 *
 * When the page is not served from localhost, localhost backend URLs are ignored and the
 * public catalog host is used instead (same idea as Voicelinto `config.js`). Override at runtime
 * with `?api_back=https://voicenavbackend.karyon.bio`.
 */

import {
  getDefaultVoiceNavApiBaseFromCatalog,
  getDefaultWidgetPublicUrlFromCatalog,
  getVoiceNavWebSocketTranscribePath,
} from '../api/apiConfig'

function trimTrailingSlash(s: string): string {
  return s.replace(/\/+$/, '')
}

function normalizeBaseUrl(s: string): string {
  return trimTrailingSlash(s.trim())
}

export function isLocalHostname(hostname: string): boolean {
  const h = hostname.trim().toLowerCase()
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
}

export function urlHostIsLocal(url: string): boolean {
  try {
    return isLocalHostname(new URL(url).hostname)
  } catch {
    return false
  }
}

function browserHostname(): string {
  if (typeof window === 'undefined') return 'localhost'
  return window.location.hostname
}

export function shouldUseCatalogBackendInsteadOfLocal(
  backendUrl: string,
  browserHost: string = browserHostname(),
): boolean {
  if (!backendUrl.trim()) return false
  if (isLocalHostname(browserHost)) return false
  return urlHostIsLocal(backendUrl)
}

function readApiBackOverride(): string {
  if (typeof window === 'undefined') return ''
  try {
    return new URLSearchParams(window.location.search).get('api_back')?.trim() || ''
  } catch {
    return ''
  }
}

function coercePublicApiBase(url: string): string {
  const normalized = normalizeBaseUrl(url)
  if (!normalized) return getDefaultVoiceNavApiBaseFromCatalog()
  if (shouldUseCatalogBackendInsteadOfLocal(normalized)) {
    return getDefaultVoiceNavApiBaseFromCatalog()
  }
  return normalized
}

function apiBaseToWssTranscribeUrl(apiHttpsBase: string): string {
  const wssOrigin = apiHttpsBase.trim().replace(/^https:\/\//i, 'wss://')
  return `${wssOrigin}${getVoiceNavWebSocketTranscribePath()}`
}

/** Map REST API origin to `/ws/transcribe` (http→ws, https→wss). */
function apiHttpBaseToWsTranscribeUrl(apiBase: string): string {
  const b = normalizeBaseUrl(apiBase)
  const wsp = getVoiceNavWebSocketTranscribePath()
  if (b.startsWith('https://')) {
    return `wss://${b.slice('https://'.length)}${wsp}`
  }
  if (b.startsWith('http://')) {
    return `ws://${b.slice('http://'.length)}${wsp}`
  }
  return apiBaseToWssTranscribeUrl(b)
}

function wsUrlFromEnv(): string {
  const fromEnv = import.meta.env.VITE_WS_BASE_URL
  const envBase = fromEnv && String(fromEnv).trim() ? normalizeBaseUrl(String(fromEnv)) : ''

  if (envBase) {
    let base = envBase
    if (base.startsWith('http://')) {
      base = `ws://${base.slice('http://'.length)}`
    } else if (base.startsWith('https://')) {
      base = `wss://${base.slice('https://'.length)}`
    }
    const pathSeg = getVoiceNavWebSocketTranscribePath()
    if (base.endsWith(pathSeg)) {
      return base
    }
    return `${base}${pathSeg}`
  }

  const fromEnvApi = import.meta.env.VITE_API_BASE_URL
  const envApi = fromEnvApi && String(fromEnvApi).trim() ? normalizeBaseUrl(String(fromEnvApi)) : ''
  if (envApi) {
    return apiHttpBaseToWsTranscribeUrl(envApi)
  }

  return apiBaseToWssTranscribeUrl(getDefaultVoiceNavApiBaseFromCatalog())
}

function apiBaseFromEnv(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL
  const envApi = fromEnv && String(fromEnv).trim() ? normalizeBaseUrl(String(fromEnv)) : ''
  if (envApi) {
    return envApi
  }
  return getDefaultVoiceNavApiBaseFromCatalog()
}

function coercePublicWebSocketUrl(url: string, apiBase: string): string {
  const normalized = normalizeBaseUrl(url)
  if (!normalized) return apiHttpBaseToWsTranscribeUrl(apiBase)
  if (shouldUseCatalogBackendInsteadOfLocal(normalized)) {
    return apiHttpBaseToWsTranscribeUrl(apiBase)
  }
  return normalized
}

/**
 * Resolved REST API base (host config, env, `?api_back=`, or catalog). Never returns a
 * localhost URL when the page itself is not on localhost.
 */
export function resolveVoiceNavApiBaseUrl(configured?: string): string {
  const override = readApiBackOverride()
  if (override) {
    return coercePublicApiBase(override)
  }
  if (configured && configured.trim()) {
    return coercePublicApiBase(configured)
  }
  return coercePublicApiBase(apiBaseFromEnv())
}

/**
 * WebSocket URL for streaming transcription (`/ws/transcribe`).
 * Uses `wss://` for secure pages; never embeds localhost when hosted remotely.
 */
export function resolveVoiceNavWebSocketTranscribeUrl(
  configured?: string,
  resolvedApiBase?: string,
): string {
  const apiBase = resolvedApiBase || resolveVoiceNavApiBaseUrl()
  const override = readApiBackOverride()
  if (override) {
    return coercePublicWebSocketUrl(apiHttpBaseToWsTranscribeUrl(override), apiBase)
  }
  if (configured && configured.trim()) {
    return coercePublicWebSocketUrl(configured, apiBase)
  }
  return coercePublicWebSocketUrl(wsUrlFromEnv(), apiBase)
}

/** @deprecated Prefer {@link resolveVoiceNavWebSocketTranscribeUrl}. */
export function getDefaultWebSocketTranscribeUrl(): string {
  return resolveVoiceNavWebSocketTranscribeUrl()
}

/** @deprecated Prefer {@link resolveVoiceNavApiBaseUrl}. */
export function getDefaultApiBaseUrl(): string {
  return resolveVoiceNavApiBaseUrl()
}

/**
 * Where the built IIFE bundle is publicly hosted (documentation / future use).
 */
export function getWidgetPublicUrl(): string | undefined {
  const fromEnv = import.meta.env.VITE_WIDGET_PUBLIC_URL
  if (fromEnv && String(fromEnv).trim()) {
    return normalizeBaseUrl(String(fromEnv))
  }

  return getDefaultWidgetPublicUrlFromCatalog()
}
